import { supabase } from '../config/supabase';
import { Hire } from '../types/hire';

/** SessionStorage key for passwordless new-hire guest session (no auth). */
export const NEW_HIRE_GUEST_KEY = 'new_hire_guest';

export interface NewHireGuestSession {
  email: string;
  id: string;
}

export function getNewHireGuestSession(): NewHireGuestSession | null {
  try {
    const raw = sessionStorage.getItem(NEW_HIRE_GUEST_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as NewHireGuestSession;
    return parsed?.email && parsed?.id ? parsed : null;
  } catch {
    return null;
  }
}

export function setNewHireGuestSession(email: string, id: string): void {
  sessionStorage.setItem(NEW_HIRE_GUEST_KEY, JSON.stringify({ email, id }));
}

export function clearNewHireGuestSession(): void {
  sessionStorage.removeItem(NEW_HIRE_GUEST_KEY);
}

  // Fetch all hires
  export interface NewHire {
  id: string;
  created_at: string;
  fullName: string;
  state: string;
  email: string;
  mobileNumber: string;
  progress: number;
  instagram: string;
  org_id: number;
  update_timestamp: string;
  checkListNum: string;
  reference: string;
  recruiter_numbers: string;
  recruiter_emails: string[];
  agent_appointment_sent?: boolean;
  notes?: string;
}

/** Guest login (no auth): get new_hire row by email if profile has role new_hire. */
export async function getNewHireForGuestLogin(email: string): Promise<NewHire | null> {
  const { data, error } = await supabase.rpc('get_new_hire_for_guest_login', {
    p_email: email.trim(),
  });
  if (error) {
    console.error('get_new_hire_for_guest_login error:', error);
    throw error;
  }
  if (data == null) return null;
  const row = data as Record<string, unknown>;
  return {
    ...row,
    progress: Number(row.progress),
  } as NewHire;
}

/** Guest onboarding: update new_hire (checkListNum, progress, update_timestamp) without auth. */
export async function updateNewHireGuest(
  id: string,
  updates: { checkListNum?: string; progress?: number; update_timestamp?: string }
): Promise<NewHire | null> {
  const p_updates: Record<string, unknown> = {};
  if (updates.checkListNum != null) p_updates.checkListNum = updates.checkListNum;
  if (updates.progress != null) p_updates.progress = updates.progress;
  if (updates.update_timestamp != null) p_updates.update_timestamp = updates.update_timestamp;

  const { data, error } = await supabase.rpc('update_new_hire_guest', {
    p_id: id,
    p_updates: p_updates,
  });
  if (error) {
    console.error('update_new_hire_guest error:', error);
    throw error;
  }
  if (data == null) return null;
  const row = data as Record<string, unknown>;
  return { ...row, progress: Number(row.progress) } as NewHire;
}

export const getNewHires = async (): Promise<NewHire[]> => {
  console.log('Fetching new hires...');
  
  // First, get the current user
  const { data: { user }, error: authError } = await supabase.auth.getUser();
  
  if (authError || !user) {
    console.error('Error getting current user:', authError);
    throw new Error('User not authenticated');
  }

  if (!user.email) {
    console.error('User email is undefined');
    throw new Error('User email is required');
  }

  console.log('Current user email:', user.email);

  // Get the current user's profile to retrieve their org_id and role
  const { data: profile, error: profileError } = await supabase
    .from('profiles')
    .select('org_id, role')
    .eq('id', user.id)
    .single();

  if (profileError || !profile) {
    console.error('Error fetching user profile:', profileError);
    throw new Error('Could not fetch user profile');
  }

  console.log('User profile:', { org_id: profile.org_id, role: profile.role });

  let query = supabase
    .from('new_hire')
    .select('*')
    .eq('org_id', profile.org_id);

  // If user is an admin, filter by recruiter_emails
  if (profile.role === 'admin') {
    console.log('User is admin, filtering by recruiter_emails containing:', user.email);
    // Search for the email within the recruiter_emails text field
    query = query.ilike('recruiter_emails', `%${user.email}%`);
  }

  // Execute the query with ordering
  const { data, error } = await query.order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching new hires:', error);
    throw error;
  }

  // Log the raw data before processing
  console.log('Raw new hires data:', data);

  // Convert progress to number for each hire and log recruiter_emails
  const processedData = data?.map(hire => {
    console.log(`Hire ${hire.id} recruiter_emails:`, hire.recruiter_emails);
    return {
      ...hire,
      progress: Number(hire.progress)
    };
  }) || [];

  console.log('Processed hires count:', processedData.length);
  console.log('Final processed data:', processedData);
  return processedData;
};

export const updateNewHire = async (id: string, updates: Partial<NewHire>) => {
  // The RLS policy will automatically check if the user is an admin
  // No need to manually verify admin status as the policy handles it
  const { data, error } = await supabase
    .from('new_hire')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating new hire:', error);
    throw error;
  }

  return data;
};

export const getArchivedHires = async (): Promise<NewHire[]> => {
  console.log('Fetching archived hires...');
  
  // First, get the current user
  const { data: { user }, error: authError } = await supabase.auth.getUser();
  
  if (authError || !user) {
    console.error('Error getting current user:', authError);
    throw new Error('User not authenticated');
  }

  // Get the current user's profile to retrieve their org_id and role
  const { data: profile, error: profileError } = await supabase
    .from('profiles')
    .select('org_id, role')
    .eq('id', user.id)
    .single();

  if (profileError || !profile) {
    console.error('Error fetching user profile:', profileError);
    throw new Error('Could not fetch user profile');
  }

  // Check if user is admin
  if (profile.role !== 'admin') {
    console.error('Access denied: User is not an admin');
    throw new Error('Access denied: Only admin users can view archived hires');
  }

  // Fetch archived hires filtered by the admin's organization
  const { data, error } = await supabase
    .from('new_hire_archive')
    .select('*')
    .eq('org_id', profile.org_id)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching archived hires:', error);
    throw error;
  }

  // Convert progress to number for each hire
  const processedData = data?.map(hire => ({
    ...hire,
    progress: Number(hire.progress)
  })) || [];

  console.log('Fetched archived hires for admin org_id:', profile.org_id, 'Data:', processedData);
  return processedData;
}; 